import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { smartMatch } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Truck, MapPin, Loader2, Sparkles, Star, Package } from 'lucide-react';
import { toast } from 'sonner';

const SmartMatchPage = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [filters, setFilters] = useState({
    vehicle_type: '',
    min_capacity_kg: '',
    min_capacity_m3: '',
    lat: '48.8566',
    lng: '2.3522',
    radius_km: '100',
    mission_description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setHasSearched(true);

    try {
      const searchFilters = {
        vehicle_type: filters.vehicle_type || undefined,
        min_capacity_kg: filters.min_capacity_kg ? parseFloat(filters.min_capacity_kg) : undefined,
        min_capacity_m3: filters.min_capacity_m3 ? parseFloat(filters.min_capacity_m3) : undefined,
        lat: filters.lat ? parseFloat(filters.lat) : undefined,
        lng: filters.lng ? parseFloat(filters.lng) : undefined,
        radius_km: filters.radius_km ? parseFloat(filters.radius_km) : 50,
        mission_description: useAI && filters.mission_description ? filters.mission_description : undefined
      };

      const data = await smartMatch(searchFilters);
      setResults(data);
      
      if (data.length === 0) {
        toast.info('Aucun véhicule trouvé avec ces critères');
      } else {
        toast.success(`${data.length} véhicule(s) trouvé(s)`);
      }
    } catch (error) {
      console.error('SmartMatch error:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const getVehicleTypeLabel = (type) => {
    const labels = {
      truck: 'Camion',
      van: 'Fourgon',
      refrigerated: 'Frigorifique',
      flatbed: 'Plateau',
      tanker: 'Citerne'
    };
    return labels[type] || type;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-slate-400';
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="smartmatch-page">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              SmartMatch
            </h1>
            {useAI && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                <Sparkles className="w-3 h-3 mr-1" />
                IA Activée
              </Badge>
            )}
          </div>
          <p className="text-slate-500">
            Trouvez les transporteurs idéaux pour vos missions grâce à l&apos;IA
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Search Form */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-500" />
                  Critères de recherche
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="space-y-4">
                  {/* AI Toggle */}
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Matching IA</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseAI(!useAI)}
                      className={`w-10 h-6 rounded-full transition-colors ${useAI ? 'bg-purple-600' : 'bg-slate-300'}`}
                      data-testid="ai-toggle"
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${useAI ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* AI Description */}
                  {useAI && (
                    <div className="space-y-2">
                      <Label>Description de la mission</Label>
                      <Textarea
                        name="mission_description"
                        value={filters.mission_description}
                        onChange={handleChange}
                        placeholder="Ex: Transport de produits pharmaceutiques sensibles nécessitant une chaîne du froid entre Paris et Lyon, 500kg, livraison urgente..."
                        rows={4}
                        className="resize-none"
                        data-testid="ai-description-input"
                      />
                      <p className="text-xs text-slate-500">
                        L&apos;IA analysera cette description pour trouver les meilleurs transporteurs
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Type de véhicule</Label>
                    <Select 
                      value={filters.vehicle_type} 
                      onValueChange={(v) => handleSelectChange('vehicle_type', v)}
                    >
                      <SelectTrigger data-testid="smartmatch-vehicle-type">
                        <SelectValue placeholder="Tous les types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="truck">Camion</SelectItem>
                        <SelectItem value="van">Fourgon</SelectItem>
                        <SelectItem value="refrigerated">Frigorifique</SelectItem>
                        <SelectItem value="flatbed">Plateau</SelectItem>
                        <SelectItem value="tanker">Citerne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Poids min (kg)</Label>
                      <Input
                        name="min_capacity_kg"
                        type="number"
                        value={filters.min_capacity_kg}
                        onChange={handleChange}
                        placeholder="1000"
                        data-testid="smartmatch-min-weight"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Volume min (m³)</Label>
                      <Input
                        name="min_capacity_m3"
                        type="number"
                        value={filters.min_capacity_m3}
                        onChange={handleChange}
                        placeholder="10"
                        data-testid="smartmatch-min-volume"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-2 block">
                      Position de référence
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Latitude</Label>
                        <Input
                          name="lat"
                          type="number"
                          value={filters.lat}
                          onChange={handleChange}
                          placeholder="48.8566"
                          step="0.0001"
                          data-testid="smartmatch-lat"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Longitude</Label>
                        <Input
                          name="lng"
                          type="number"
                          value={filters.lng}
                          onChange={handleChange}
                          placeholder="2.3522"
                          step="0.0001"
                          data-testid="smartmatch-lng"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 mt-3">
                      <Label>Rayon (km)</Label>
                      <Input
                        name="radius_km"
                        type="number"
                        value={filters.radius_km}
                        onChange={handleChange}
                        placeholder="100"
                        data-testid="smartmatch-radius"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={loading}
                    data-testid="smartmatch-search-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {useAI ? 'Analyse IA en cours...' : 'Recherche...'}
                      </>
                    ) : (
                      <>
                        {useAI && <Sparkles className="w-4 h-4 mr-2" />}
                        <Search className="w-4 h-4 mr-2" />
                        Rechercher
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo']">
                  Résultats {hasSearched && `(${results.length})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!hasSearched ? (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      Définissez vos critères et lancez une recherche
                    </p>
                  </div>
                ) : loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-4" />
                    {useAI && (
                      <p className="text-sm text-purple-600">
                        L&apos;IA analyse les profils des transporteurs...
                      </p>
                    )}
                  </div>
                ) : results.length > 0 ? (
                  <div className="space-y-4">
                    {results.map((result, index) => (
                      <div 
                        key={result.vehicle.id}
                        className="p-4 border border-slate-200 rounded-lg hover:border-orange-200 transition-colors"
                        data-testid={`smartmatch-result-${result.vehicle.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                              <Truck className="w-6 h-6 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-slate-900">{result.vehicle.name}</h3>
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  Disponible
                                </Badge>
                                <Badge variant="outline">
                                  {getVehicleTypeLabel(result.vehicle.vehicle_type)}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600 mb-2">
                                {result.carrier_name} • {result.vehicle.license_plate}
                              </p>
                              
                              {/* Rating */}
                              <div className="flex items-center gap-2 mb-2">
                                {renderStars(result.carrier_rating)}
                                <span className="text-sm text-slate-500">
                                  ({result.carrier_rating.toFixed(1)})
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-slate-600">
                                  <span className="font-['JetBrains_Mono'] text-slate-900">{result.vehicle.capacity_kg}</span> kg
                                </span>
                                <span className="text-slate-600">
                                  <span className="font-['JetBrains_Mono'] text-slate-900">{result.vehicle.capacity_m3}</span> m³
                                </span>
                                {result.distance_km && (
                                  <span className="flex items-center gap-1 text-blue-600">
                                    <MapPin className="w-3.5 h-3.5" />
                                    <span className="font-['JetBrains_Mono']">{result.distance_km}</span> km
                                  </span>
                                )}
                              </div>
                              
                              {result.vehicle.certifications?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {result.vehicle.certifications.map((cert, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                                  ))}
                                </div>
                              )}
                              
                              {/* AI Recommendation */}
                              {result.ai_recommendation && (
                                <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                  <div className="flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                                    <p className="text-sm text-purple-800">{result.ai_recommendation}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="text-center shrink-0">
                            <div className={`w-14 h-14 rounded-full ${getScoreColor(result.match_score)} flex items-center justify-center`}>
                              <span className="text-white font-bold font-['JetBrains_Mono']">
                                {Math.round(result.match_score)}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Score</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun résultat</h3>
                    <p className="text-slate-500">
                      Essayez avec des critères différents
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SmartMatchPage;
